import React, { useState, useRef, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";

import {
	Grid,
	Paper,
	Typography,
	Button,
	Tabs,
	Tab,
	ListItem,
	ListItemText,
	Box,
	IconButton,
	Badge
} from "@mui/material";
import { FixedSizeList as List } from "react-window";

import { AiOutlineDoubleLeft, AiOutlineDoubleRight } from "react-icons/ai";
import { MdEmail, MdSms, MdNotifications, MdSettings, MdPeople } from "react-icons/md";
import { FaWhatsapp, FaFileImport } from "react-icons/fa";

import XLSX from "xlsx";

import ls from "Localization";

import TextInput from "Components/TextInput";

import {
	setValue,
	clearValues,
	editMarketing,
	getMarketing,
	importUsers
} from "store/actions/marketing";

import ImportStatusModal from "Components/ImportStatusModal";

import useStyles from "./styles";
import { DateTimePicker } from "@mui/x-date-pickers";
import { handleChangeEvent, CustomFile } from "helpers/files";
import { getUsers } from "store/actions/user";
import Select from "Components/Select";
import PushAction from "Types/PushAction";
import Checkbox from "Components/Checkbox";
import { useNavigate, useParams } from "react-router-dom";
import { removeDiacritics } from "helpers/string";

const ls2: any = ls;

function TabPanel(props: any) {
	const { children, value, index, ...other } = props;

	return (
		<Typography
			component="div"
			role="tabpanel"
			hidden={value !== index}
			id={`scrollable-auto-tabpanel-${index}`}
			aria-labelledby={`scrollable-auto-tab-${index}`}
			{...other}
		>
			{value === index && <Box p={3}>{children}</Box>}
		</Typography>
	);
}

const handleFilterUsers = (c: any, value: any) =>
	`${c.fullName.toLowerCase()} - ${c.business.business.displayName.toLowerCase()}`.indexOf(
		value.toLowerCase()
	) !== -1;

export function MarketingEdit() {
	const navigate = useNavigate();
	const { id } = useParams();
	const classes = useStyles({});
	const dispatch = useDispatch<any>();
	const [tab, setTab] = useState(0);
	const [whatsAppFile, setWhatsAppFile] = useState<CustomFile | null>(null);
	const inputFileRef = useRef(null);
	const emailImportFileRef = useRef<HTMLInputElement>(null);
	const [filterIn, setFilterIn] = useState("");
	const [filterOut, setFilterOut] = useState("");
	const [importModalOpen, setImportModalOpen] = useState(false);
	const [importStatus, setImportStatus] = useState<"idle" | "importing" | "success" | "error">("idle");
	const [importProgress, setImportProgress] = useState<any>(null);
	const [importResults, setImportResults] = useState<any>(null);
	const [importError, setImportError] = useState<string>("");

	const [channelFilter, setChannelFilter] = useState("hasAnyChannel");

	useEffect(() => {
		dispatch(clearValues());
		if (id) {
			dispatch(getMarketing(id));
		}
		
		const filters = `&filters[${channelFilter}]=true`;
		dispatch(getUsers(0, 1000, filters));
	}, [dispatch, id, channelFilter]);

	const {
		name,	
		startAt,
		economicMode,
		emailSubject,
		emailMessage,
		smsMessage,
		whatsAppMessage,
		whatsAppRestrict,
		pushTitle,
		pushMessage,
		pushAction,
		pushValue,
		users,
		errors
	} = useSelector<any, any>(s => s.marketing);

	const usersState = useSelector<any, any>(s => s.userReport);

	const handleChange = (name: string, value: any) => {
		// Auto-format email content to preserve line breaks
		if (name === "emailMessage" && value) {
			value = formatEmailContent(value);
		}
		
		dispatch(
			setValue({
				[name]: value
			})
		);
	};

	const handleRight = () => {
		const newUsers = (usersState?.enabled || [])
			.filter(
				(c: any) =>
					!users.find((u: any) => u.userId === c.id) &&
					handleFilterUsers(c, filterOut)
			)
			.map((c: any) => ({ userId: c.id }));

		handleChange("users", [...users, ...newUsers]);

		setFilterOut("");
	};

	const handleLeft = () => {
		let keep = [];
		if (filterIn) {
			const filtered = (usersState?.enabled || []).filter((c: any) =>
				handleFilterUsers(c, filterIn)
			);

			keep = users.filter(
				(u: any) => !filtered.find((c: any) => c.id === u.userId)
			);
		}

		handleChange("users", keep);
		setFilterIn("");
	};

	const handleUserChange = (items: any[], index: number) => {
		const item = users.find((c: any) => c.userId === items[index].id);

		if (item) {
			handleChange(
				"users",
				users.filter((c: any) => c !== item)
			);
		} else {
			handleChange("users", [...users, { userId: items[index].id }]);
		}
	};

	const handleCloseImportModal = () => {
		setImportModalOpen(false);
		setImportStatus("idle");
		setImportProgress(null);
		setImportResults(null);
		setImportError("");
	};

	// Function to extract variables from text
	const extractVariables = (text: string): string[] => {
		if (!text) return [];
		const variableRegex = /\{\{([^}]+)\}\}/g;
		const variables: string[] = [];
		let match;
		while ((match = variableRegex.exec(text)) !== null) {
			variables.push(match[1]);
		}
		return Array.from(new Set(variables)); // Remove duplicates
	};

	// Function to validate variables against custom variables
	const validateVariables = (): { isValid: boolean; missingVariables: string[] } => {
		const allVariables: string[] = [];
		const availableVariables = new Set<string>();

		// Collect all variables from messages
		if (emailSubject) allVariables.push(...extractVariables(emailSubject));
		if (emailMessage) allVariables.push(...extractVariables(emailMessage));
		if (smsMessage) allVariables.push(...extractVariables(smsMessage));
		if (whatsAppMessage) allVariables.push(...extractVariables(whatsAppMessage));
		if (pushTitle) allVariables.push(...extractVariables(pushTitle));
		if (pushMessage) allVariables.push(...extractVariables(pushMessage));

		// Collect available variables from all users
		users.forEach((userItem: any) => {
			if (userItem.customVariables) {
				Object.keys(userItem.customVariables).forEach(key => {
					availableVariables.add(key);
				});
			}
		});

		// Find missing variables
		const uniqueVariables = Array.from(new Set(allVariables));
		const missingVariables = uniqueVariables.filter(variable => !availableVariables.has(variable));

		return {
			isValid: missingVariables.length === 0,
			missingVariables
		};
	};

	const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();

		// Validate variables before submitting
		const validation = validateVariables();
		if (!validation.isValid) {
			const missingList = validation.missingVariables.join(', ');
			alert(`❌ Variáveis não encontradas nos dados dos usuários:\n\n${missingList}\n\nPor favor, verifique se todas as variáveis usadas nas mensagens estão disponíveis nos dados importados dos usuários.`);
			return;
		}

		dispatch(
			editMarketing(whatsAppFile, (err, model) => {
				if (err) {
					console.log(err);
					if (err.default) alert(err.default);
					else if (typeof err === "string") alert(err);
					else alert(JSON.stringify(err));
				} else {
					navigate(`/marketing/${model.id ? model.id : model}`);
				}
			})
		);
	};

	const selectFile = (e: React.ChangeEvent<HTMLInputElement>) => {
		const files = handleChangeEvent(e);

		if (files && files.length) {
			const file = files[0];

			setWhatsAppFile(file);
		}
	};

	const openSelectFile = () => {
		if (emailImportFileRef.current) {
			emailImportFileRef.current.click();
		}
	};

	const handleEmailFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];

		if (!file) return;

		e.target.value = "";

		const reader = new FileReader();
		const rABS = !!reader.readAsBinaryString;
		reader.onload = async (e: any) => {
			/* Parse data */
			const bstr = e.target.result;
			const wb = XLSX.read(bstr, { type: rABS ? "binary" : "array" });
			/* Get first worksheet */
			const wsname = wb.SheetNames[0];
			const sheet: any = wb.Sheets[wsname];

			const json = XLSX.utils.sheet_to_json(sheet, { 
				raw: false,
				defval: ""
			});

			// Process the imported data and extract custom variables
			const importedEmails = json.map((row: any) => {
				// Normalize column names for matching (case-insensitive, no diacritics)
				const normalizedRow: any = {};
				Object.keys(row).forEach(key => {
					const normalizedKey = removeDiacritics(key.trim().toLowerCase());
					normalizedRow[normalizedKey] = row[key];
				});

				const email = String(normalizedRow["email"] || normalizedRow["e-mail"] || "").trim();
				const fullName = String(normalizedRow["nome"] || normalizedRow["name"] || normalizedRow["titulo"] || "").trim();
				const cpf = String(normalizedRow["cpf"] || "").trim();
				const cnpj = String(normalizedRow["cnpj"] || "").trim();
				const phone = String(normalizedRow["telefone"] || normalizedRow["phone"] || "").trim();
				const whatsAppEnabled = Boolean(normalizedRow["whatsapp"] || false);
				
				const customVariables: Record<string, string> = {};

				Object.keys(row).forEach(key => {					
					if (row[key]?.toString() && row[key].toString().trim() !== "") {
						// Clean up XLSX suffixes (_1, _2, etc.) from column names
						const cleanKey = key.replace(/_\d+$/, '');
						customVariables[cleanKey] = row[key].toString().trim();
					}
				});

				const name = fullName?.split(" ")[0]?.trim() || "";
				const lastname = fullName?.split(" ").slice(1).join(" ")?.trim() || "";

				return {
					email,
					name,
					lastname,
					fullName,
					cpf,
					cnpj,
					phone,
					whatsAppEnabled,
					customVariables
				};
			}).filter((item: any) => item.email && item.email.trim() !== "");

			// Call API to import users using action
			const usersToImport = importedEmails.map(item => ({
				email: item.email,
				name: item.name,
				lastname: item.lastname,
				cpf: item.cpf,
				cnpj: item.cnpj,
				phone: item.phone,
				whatsAppEnabled: item.whatsAppEnabled,
				customVariables: item.customVariables
			}));

			// Initialize import modal
			setImportStatus("importing");
			setImportModalOpen(true);
			setImportProgress({
				currentChunk: 0,
				totalChunks: Math.ceil(usersToImport.length / 500),
				processedUsers: 0,
				totalUsers: usersToImport.length
			});

			dispatch(importUsers(usersToImport, (err: any, result: any) => {
				if (err) {
					setImportStatus("error");
					setImportError(err.default || "Erro desconhecido");
				} else {
					// Add processed users to the campaign's recipient list using real user IDs
					const importedUserObjects = result.processedUsers.map((processedUser: any) => {
						// Find the corresponding email data to get custom variables
						const emailData = importedEmails.find((email: any) => 
							email.email.toLowerCase() === processedUser.email.toLowerCase()
						);
						
						return {
							user: {
								_id: processedUser._id, // Use real user ID from API
								email: processedUser.email,
								name: processedUser.name,
								lastname: processedUser.lastname,
								cpf: processedUser.cpf,
								cnpj: processedUser.cnpj,
								phone: processedUser.phoneNumber,
								whatsAppEnabled: processedUser.whatsAppEnabled,
								isImported: true
							},
							customVariables: emailData?.customVariables || {}
						};
					});

					// Add to campaign recipients with deduplication
					// Create a map to track existing users by their identifiers
					const existingUserMap = new Map();
					users.forEach((user: any) => {
						const identifier = user.user?.email || user.user?._id || user.userId;
						if (identifier) {
							existingUserMap.set(identifier, user);
						}
					});

					// Filter out duplicates from imported users
					const uniqueImportedUsers = importedUserObjects.filter((importedUser: any) => {
						const identifier = importedUser.user?.email || importedUser.user?._id;
						return !existingUserMap.has(identifier);
					});

					// Add only unique users to campaign recipients
					const updatedUsers = [...users, ...uniqueImportedUsers];
					handleChange("users", updatedUsers);

					// Set success status with results
					setImportStatus("success");
					setImportResults({
						totalProcessed: result.totalProcessed,
						totalChunks: result.totalChunks,
						errors: result.errors || [],
						customVariablesCount: Object.keys(importedEmails[0]?.customVariables || {}).length,
						duplicatesFiltered: importedUserObjects.length - uniqueImportedUsers.length,
						uniqueUsersAdded: uniqueImportedUsers.length
					});
					
					// Refresh user list to include newly imported users in the available users list
					const filters = `&filters[${channelFilter}]=true`;
					
					dispatch(getUsers(0, 1000, filters));
				}
			}, (progress: any) => {
				// Update progress in real-time
				setImportProgress(progress);
			}));
		};

		if (rABS) reader.readAsBinaryString(file);
		else reader.readAsArrayBuffer(file);

		if (emailImportFileRef.current) {
			emailImportFileRef.current.value = "";
		}
	};

	// Function to preserve line breaks in email content
	const formatEmailContent = (content: string) => {
		if (!content) return "";
		
		// Convert line breaks to <br> tags while preserving existing HTML
		return content
			.split('\n')
			.map((line, index) => {
				// If the line already contains HTML tags, don't add <br> to it
				if (line.includes('<') && line.includes('>')) {
					return line;
				}
				// Add <br> tag for line breaks, except for the last line
				return index < content.split('\n').length - 1 ? line + '<br>' : line;
			})
			.join('');
	};

	const renderRow = (items: any, props: any) => {
		const { index, style } = props;

		if (!items.length)
			return (
				<ListItem button style={style} key={index}>
					<ListItemText
						style={{
							whiteSpace: "nowrap",
							maxWidth: 400
						}}
						primary="Nenhum item"
					/>
				</ListItem>
			);

		return (
			<ListItem
				button
				style={style}
				key={index}
				onClick={() => handleUserChange(items, index)}
			>
				<ListItemText
					style={{
						whiteSpace: "nowrap",
						maxWidth: 400
					}}
					primary={`${items[index].fullName} - ${items[index].business.business.name}`}
				/>
			</ListItem>
		);
	};

	const filtredIn = (usersState?.enabled || []).filter((c: any) =>
		handleFilterUsers(c, filterIn)
	);

	const filtredOut = (usersState?.enabled || []).filter((c: any) =>
		handleFilterUsers(c, filterOut)
	);

	const usersIn = filtredIn.filter((c: any) =>
		users.find((u: any) => u.userId === c.id)
	);

	const usersNotIn = filtredOut.filter(
		(c: any) => !users.find((u: any) => u.userId === c.id)
	);

	return (
		<Grid container justifyContent="center" spacing={5}>
			<Grid item xs={11}>
				<Paper className={classes.root}>
					<Box sx={{ textAlign: 'center', mb: 3 }}>
						<Typography variant="h4" component="h1" gutterBottom>
							✏️ Editar Campanha de Marketing
						</Typography>
						<Typography variant="body1" color="text.secondary">
							Modifique sua campanha e ajuste as configurações para otimizar o engajamento
						</Typography>
					</Box>

					<form noValidate onSubmit={onSubmit}>
						<Tabs
							value={tab}
							indicatorColor="primary"
							textColor="primary"
							onChange={(_, v) => setTab(v)}
							variant="scrollable"
							scrollButtons="auto"
							sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
						>
							<Tab 
								icon={<MdSettings />} 
								label="Configurações" 
								iconPosition="start"
								sx={{ minHeight: 48 }}
							/>
							<Tab
								icon={<MdEmail />}
								iconPosition="start"
								sx={{ minHeight: 48 }}
								label={
									<Badge
										color="secondary"
										badgeContent={usersState?.email || 0}
										max={99999}
									>
										<Typography>E-mail</Typography>
									</Badge>
								}
							/>
							<Tab
								icon={<MdSms />}
								iconPosition="start"
								sx={{ minHeight: 48 }}
								label={
									<Badge
										color="secondary"
										badgeContent={usersState?.sms || 0}
										max={99999}
									>
										<Typography>SMS</Typography>
									</Badge>
								}
							/>
							<Tab
								icon={<FaWhatsapp />}
								iconPosition="start"
								sx={{ minHeight: 48 }}
								label={
									<Badge
										color="secondary"
										badgeContent={usersState?.whatsApp || 0}
										max={99999}
									>
										<Typography>WhatsApp</Typography>
									</Badge>
								}
							/>
							<Tab
								icon={<MdNotifications />}
								iconPosition="start"
								sx={{ minHeight: 48 }}
								label={
									<Badge
										color="secondary"
										badgeContent={usersState?.push || 0}
										max={99999}
									>
										<Typography>Push</Typography>
									</Badge>
								}
							/>
							<Tab 
								icon={<MdPeople />} 
								label="Destinatários" 
								iconPosition="start"
								sx={{ minHeight: 48 }}
							/>
						</Tabs>
						<TabPanel value={tab} index={0}>
							<Box sx={{ mb: 2 }}>
								<Typography variant="h6" gutterBottom>
									🎯 Configurações da Campanha
								</Typography>
								<Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
									Modifique as informações básicas da sua campanha de marketing
								</Typography>
							</Box>
							
							<Grid container spacing={3} className={classes.form}>
								<Grid item xs={12}>
									<Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'medium' }}>
										📝 Informações Básicas
									</Typography>
								</Grid>
								
								<Grid item xs={12} md={8}>
									<TextInput
										id="name"
										required
										variant="outlined"
										name="name"
										value={name}
										errors={errors}
										onChange={handleChange}
										placeholder="Ex: Promoção Black Friday 2024"
										helperText="Nome identificador da campanha (máx. 100 caracteres)"
									/>
								</Grid>
								
								<Grid item xs={12} md={4}>
									<DateTimePicker
										value={startAt}
										label={ls.startAt}
										onChange={date => handleChange("startAt", date)}
										format={ls.dateFormatWithoutSeconds}
										ampm={false}
										slotProps={{
											textField: {
												helperText: "Quando a campanha será enviada"
											}
										}}
									/>
								</Grid>
								
								<Grid item xs={12}>
									<Typography variant="subtitle1" sx={{ mb: 2, mt: 2, fontWeight: 'medium' }}>
										⚙️ Configurações Avançadas
									</Typography>
								</Grid>
								
								<Grid item xs={12}>
									<Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
										<Checkbox
											id="economicMode"
											name="economicMode"
											checked={economicMode}
											onChange={handleChange}
										/>
										<Box>
											<Typography variant="body1" sx={{ fontWeight: 'medium' }}>
												Modo Econômico
											</Typography>
											<Typography variant="body2" color="text.secondary">
												Reduz custos priorizando canais mais baratos (SMS {'>'} Email {'>'} WhatsApp {'>'} Push)
											</Typography>
										</Box>
									</Box>
								</Grid>
								
								<Grid item xs={12}>
									<Box sx={{ 
										p: 2, 
										bgcolor: 'info.light', 
										borderRadius: 1, 
										border: '1px solid',
										borderColor: 'info.main',
										color: 'info.contrastText'
									}}>
										<Typography variant="body2">
											💡 <strong>Dica:</strong> Após editar as configurações, revise as mensagens nas abas específicas para garantir consistência.
										</Typography>
									</Box>
								</Grid>
							</Grid>
						</TabPanel>

						<TabPanel value={tab} index={1}>
							<Box sx={{ mb: 2 }}>
								<Typography variant="h6" gutterBottom>
									📧 Configuração de E-mail
								</Typography>
								<Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
									Edite o assunto e conteúdo do e-mail da sua campanha
								</Typography>
							</Box>
							
							<Grid container spacing={3} className={classes.form}>
								<Grid item xs={12}>
									<Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'medium' }}>
										📝 Assunto do E-mail
									</Typography>
									<TextInput
										id="emailSubject"
										required
										variant="outlined"
										name="emailSubject"
										value={emailSubject}
										errors={errors}
										onChange={handleChange}
										placeholder="Ex: 🎉 Oferta Especial - Desconto de 50%!"
										helperText={`${emailSubject?.length || 0}/78 caracteres (recomendado para melhor visualização)`}
									/>
								</Grid>
								
								<Grid item xs={12}>
									<Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'medium' }}>
										✉️ Conteúdo do E-mail
									</Typography>
									<TextInput
										id="emailMessage"
										required
										variant="outlined"
										name="emailMessage"
										value={emailMessage ? emailMessage.replace(/<br\s*\/?>/gi, '\n') : ""}
										multiline
										rows={8}
										errors={errors}
										onChange={handleChange}
										placeholder="Digite aqui o conteúdo HTML do seu e-mail..."
										helperText="Você pode usar HTML para formatação (negrito, links, imagens, etc.). Quebras de linha são preservadas automaticamente."
									/>
								</Grid>
								
								<Grid item xs={12}>
									<Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'medium' }}>
										👀 Preview do E-mail
									</Typography>
									<Box sx={{ 
										border: '1px solid #ddd', 
										borderRadius: 1, 
										p: 2, 
										bgcolor: '#fff',
										minHeight: 200,
										overflow: 'auto'
									}}>
										{emailMessage ? (
											<div dangerouslySetInnerHTML={{ __html: emailMessage }} />
										) : (
											<Typography color="text.secondary" style={{ fontStyle: 'italic' }}>
												O preview aparecerá aqui conforme você digita...
											</Typography>
										)}
									</Box>
								</Grid>
								
								<Grid item xs={12}>
									<Box sx={{ 
										p: 2, 
										bgcolor: 'warning.light', 
										borderRadius: 1, 
										border: '1px solid',
										borderColor: 'warning.main'
									}}>
										<Typography variant="body2">
											⚠️ <strong>Dicas importantes:</strong>
											<br />• Mantenha o assunto claro e atrativo (máx. 78 caracteres)
											<br />• Use HTML válido para garantir boa renderização
											<br />• Quebras de linha são preservadas automaticamente
											<br />• Teste em diferentes clientes de e-mail antes do envio
										</Typography>
									</Box>
								</Grid>
							</Grid>
						</TabPanel>

						<TabPanel value={tab} index={2}>
							<Box sx={{ mb: 2 }}>
								<Typography variant="h6" gutterBottom>
									📱 Configuração de SMS
								</Typography>
								<Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
									Edite a mensagem SMS da sua campanha (máximo 140 caracteres). Use variáveis como {'{{nome}}'}, {'{{empresa}}'}, etc.
								</Typography>
							</Box>
							
							<Grid container spacing={3} className={classes.form}>
								<Grid item xs={12}>
									<Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'medium' }}>
										💬 Mensagem SMS
									</Typography>
									<TextInput
										id="smsMessage"
										required
										variant="outlined"
										name="smsMessage"
										value={smsMessage}
										errors={errors}
										multiline
										rows={4}
										onChange={handleChange}
										placeholder="Ex: 🎉 Oferta especial! 50% OFF em todos os produtos. Use o código: PROMO50. Válido até 31/12. Link: bit.ly/oferta"
										FormHelperTextProps={{ 
											style: { 
												textAlign: "right",
												color: smsMessage?.length > 140 ? '#d32f2f' : '#666'
											} 
										}}
										helperText={`${smsMessage?.length || 0}/140 caracteres`}
									/>
								</Grid>
								
								<Grid item xs={12}>
									<Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'medium' }}>
										📋 Preview SMS
									</Typography>
									<Box sx={{ 
										border: '1px solid #ddd', 
										borderRadius: 2, 
										p: 2, 
										bgcolor: '#f5f5f5',
										fontFamily: 'monospace',
										fontSize: '14px',
										minHeight: 80,
										position: 'relative'
									}}>
										<Box sx={{ 
											position: 'absolute', 
											top: 8, 
											right: 8, 
											fontSize: '12px', 
											color: '#999' 
										}}>
											📱 SMS
										</Box>
										{smsMessage ? (
											<Typography component="div" sx={{ mt: 2 }}>
												{smsMessage}
											</Typography>
										) : (
											<Typography color="text.secondary" style={{ fontStyle: 'italic', marginTop: 16 }}>
												Sua mensagem SMS aparecerá aqui...
											</Typography>
										)}
									</Box>
								</Grid>
								
								<Grid item xs={12}>
									<Box sx={{ 
										p: 2, 
										bgcolor: 'info.light', 
										borderRadius: 1, 
										border: '1px solid',
										borderColor: 'info.main'
									}}>
										<Typography variant="body2">
											💡 <strong>Dicas para SMS eficazes:</strong>
											<br />• Seja conciso e direto - você tem apenas 140 caracteres
											<br />• Inclua uma chamada para ação clara
											<br />• Use emojis para chamar atenção (mas com moderação)
											<br />• Considere incluir um link encurtado se necessário
										</Typography>
									</Box>
								</Grid>
							</Grid>
						</TabPanel>

						<TabPanel value={tab} index={3}>
							<Box sx={{ mb: 2 }}>
								<Typography variant="h6" gutterBottom>
									💚 Configuração do WhatsApp
								</Typography>
								<Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
									Edite a mensagem do WhatsApp e opções de envio. Use variáveis como {'{{nome}}'}, {'{{empresa}}'}, etc.
								</Typography>
							</Box>
							
							<Grid container spacing={3} className={classes.form}>
								<Grid item xs={12}>
									<Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'medium' }}>
										💬 Mensagem WhatsApp
									</Typography>
									<TextInput
										id="whatsAppMessage"
										required
										variant="outlined"
										name="whatsAppMessage"
										value={whatsAppMessage}
										errors={errors}
										multiline
										rows={6}
										onChange={handleChange}
										placeholder="Ex: 🎉 *Oferta Especial!* 
									
Olá! Temos uma promoção imperdível para você:
									
✅ 50% de desconto em todos os produtos
✅ Frete grátis para todo o Brasil
✅ Válido até 31/12/2024
									
Para aproveitar, acesse: https://meusite.com/promo
									
Não perca essa oportunidade! 🚀"
										helperText={`${whatsAppMessage?.length || 0} caracteres - WhatsApp permite mensagens longas`}
									/>
								</Grid>
								
								<Grid item xs={12}>
									<Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'medium' }}>
										⚙️ Configurações do WhatsApp
									</Typography>
									<Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
										<Checkbox
											id="whatsAppRestrict"
											name="whatsAppRestrict"
											checked={whatsAppRestrict}
											onChange={handleChange}
										/>
										<Box>
											<Typography variant="body1" sx={{ fontWeight: 'medium' }}>
												Modo Restrito
											</Typography>
											<Typography variant="body2" color="text.secondary">
												Envia apenas para usuários que autorizaram receber mensagens comerciais via WhatsApp
											</Typography>
										</Box>
									</Box>
								</Grid>
								
								<Grid item xs={12}>
									<Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'medium' }}>
										📱 Preview WhatsApp
									</Typography>
									<Box sx={{ 
										border: '1px solid #25d366', 
										borderRadius: 2, 
										p: 2, 
										bgcolor: '#f0f8f0',
										position: 'relative',
										maxWidth: 400
									}}>
										<Box sx={{ 
											display: 'flex',
											alignItems: 'center',
											gap: 1,
											mb: 2,
											fontSize: '14px',
											fontWeight: 'bold',
											color: '#25d366'
										}}>
											💚 WhatsApp Business
										</Box>
										{whatsAppMessage ? (
											<Typography component="div" sx={{ 
												fontSize: '14px',
												lineHeight: 1.4,
												whiteSpace: 'pre-line'
											}}>
												{whatsAppMessage}
											</Typography>
										) : (
											<Typography color="text.secondary" style={{ fontStyle: 'italic' }}>
												Sua mensagem do WhatsApp aparecerá aqui...
											</Typography>
										)}
									</Box>
								</Grid>
								
								<Grid item xs={12}>
									<Box sx={{ 
										p: 2, 
										bgcolor: 'success.light', 
										borderRadius: 1, 
										border: '1px solid',
										borderColor: 'success.main'
									}}>
										<Typography variant="body2">
											💡 <strong>Dicas para WhatsApp:</strong>
											<br />• Use formatação: *negrito*, _itálico_, ~riscado~
											<br />• Emojis tornam a mensagem mais amigável
											<br />• Quebras de linha ajudam na organização
											<br />• Links são automaticamente clicáveis
											<br />• Respeite a LGPD - use o modo restrito quando apropriado
										</Typography>
									</Box>
								</Grid>
							</Grid>
						</TabPanel>

						<TabPanel value={tab} index={4}>
							<Box sx={{ mb: 2 }}>
								<Typography variant="h6" gutterBottom>
									🔔 Configuração de Push Notification
								</Typography>
								<Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
									Edite as notificações push para dispositivos móveis
								</Typography>
							</Box>
							
							<Grid container spacing={3} className={classes.form}>
								<Grid item xs={12}>
									<Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'medium' }}>
										📝 Título da Notificação
									</Typography>
									<TextInput
										id="pushTitle"
										required
										variant="outlined"
										name="pushTitle"
										value={pushTitle}
										errors={errors}
										onChange={handleChange}
										placeholder="Ex: Oferta Especial! 🎉"
										FormHelperTextProps={{ 
											style: { 
												textAlign: "right",
												color: pushTitle?.length > 44 ? '#d32f2f' : '#666'
											} 
										}}
										helperText={`${pushTitle?.length || 0}/44 caracteres (recomendado)`}
									/>
								</Grid>
								
								<Grid item xs={12}>
									<Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'medium' }}>
										💬 Mensagem da Notificação
									</Typography>
									<TextInput
										id="pushMessage"
										required
										variant="outlined"
										name="pushMessage"
										value={pushMessage}
										errors={errors}
										multiline
										rows={4}
										onChange={handleChange}
										placeholder="Ex: Aproveite 50% de desconto em todos os produtos. Oferta válida por tempo limitado!"
										FormHelperTextProps={{ 
											style: { 
												textAlign: "right",
												color: pushMessage?.length > 178 ? '#d32f2f' : '#666'
											} 
										}}
										helperText={`${pushMessage?.length || 0}/178 caracteres (recomendado)`}
									/>
								</Grid>
								
								<Grid item xs={12}>
									<Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'medium' }}>
										🎯 Ação da Notificação
									</Typography>
								</Grid>
								
								<Grid item xs={12} md={6}>
									<Select
										errors={errors}
										id="pushAction"
										name="action"
										required
										value={pushAction}
										onChange={handleChange}
										options={Object.keys(PushAction).map((c: any) => ({
											id: c,
											value: ls2[PushAction[c]]
										}))}
									/>
								</Grid>
								
								<Grid item xs={12} md={6}>
									<TextInput
										id="pushValue"
										variant="outlined"
										name="value"
										value={pushValue}
										errors={errors}
										onChange={handleChange}
										placeholder="URL ou valor da ação"
										helperText="Defina para onde o usuário será direcionado ao tocar na notificação"
									/>
								</Grid>
								
								<Grid item xs={12}>
									<Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'medium' }}>
										📱 Preview da Notificação
									</Typography>
									<Box sx={{ 
										border: '1px solid #1976d2', 
										borderRadius: 2, 
										p: 2, 
										bgcolor: '#f5f7fa',
										maxWidth: 350,
										position: 'relative'
									}}>
										<Box sx={{ 
											display: 'flex',
											alignItems: 'center',
											gap: 1,
											mb: 1,
											fontSize: '12px',
											color: '#666'
										}}>
											🔔 Push Notification
										</Box>
										<Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
											{pushTitle || 'Título da notificação'}
										</Typography>
										<Typography variant="body2" color="text.secondary">
											{pushMessage || 'Mensagem da notificação aparecerá aqui...'}
										</Typography>
									</Box>
								</Grid>
								
								<Grid item xs={12}>
									<Box sx={{ 
										p: 2, 
										bgcolor: 'primary.light', 
										borderRadius: 1, 
										border: '1px solid',
										borderColor: 'primary.main',
										color: 'primary.contrastText'
									}}>
										<Typography variant="body2">
											📱 <strong>Dicas para Push Notifications:</strong>
											<br />• Seja conciso - títulos curtos têm maior impacto
											<br />• Use emojis para chamar atenção
											<br />• Defina uma ação clara (URL, tela do app, etc.)
											<br />• Teste em diferentes dispositivos
											<br />• Considere o horário ideal para envio
										</Typography>
									</Box>
								</Grid>
							</Grid>
						</TabPanel>

						<TabPanel value={tab} index={5}>
							<Box sx={{ mb: 2 }}>
								<Typography variant="h6" gutterBottom>
									👥 Seleção de Destinatários
								</Typography>
								<Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
									Gerencie os usuários que receberão sua campanha de marketing
								</Typography>
							</Box>

							<Grid container spacing={3} className={classes.form}>
								<Grid item xs={12}>
									<Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'medium' }}>
										🔧 Configurações de Filtro
									</Typography>
								</Grid>

								<Grid item xs={12} md={6}>
									<Select
										errors={errors}
										id="channelFilter"
										name="channelFilter"
										label="Canal de Comunicação"
										value={channelFilter}
										onChange={(_, v) => setChannelFilter(v)}
										options={[
											{
												id: "hasAnyChannel",
												value: "Qualquer Canal (Email, SMS, WhatsApp, Push)"
											},
											{
												id: "hasEmail",
												value: "Apenas com Email"
											},
											{
												id: "hasPhone",
												value: "Apenas com Telefone"
											},
											{
												id: "hasWhatsApp",
												value: "Apenas com WhatsApp"
											},
											{
												id: "hasPush",
												value: "Apenas com Push Notifications"
											}
										]}
									/>
								</Grid>

								<Grid item xs={12} md={6}>
									<Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
										<Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
											📁 Importar Lista de E-mails
										</Typography>
										<IconButton 
											onClick={openSelectFile}
											sx={{ 
												bgcolor: 'primary.main', 
												color: 'white',
												'&:hover': { bgcolor: 'primary.dark' }
											}}
										>
											<FaFileImport />
										</IconButton>
									</Box>
									<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
										Importe uma planilha XLSX com colunas: email, nome (opcional). Colunas adicionais serão usadas como variáveis personalizadas.
									</Typography>
									<Box sx={{ mt: 1, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
										<Typography variant="body2" sx={{ fontWeight: 'medium', mb: 1 }}>
											💡 Como usar variáveis personalizadas:
										</Typography>
										<Box sx={{ fontSize: '0.875rem' }}>
											<Typography variant="body2" component="div">
												• Use <code style={{ background: '#fff', padding: '2px 4px', borderRadius: '3px' }}>{'{{nome}}'}</code> para incluir o nome do cliente
											</Typography>
											<Typography variant="body2" component="div">
												• Use <code style={{ background: '#fff', padding: '2px 4px', borderRadius: '3px' }}>{'{{empresa}}'}</code> para incluir o nome da empresa
											</Typography>
											<Typography variant="body2" component="div">
												• Use <code style={{ background: 'white', padding: '2px 4px', borderRadius: '3px' }}>{'{{valor}}'}</code> para incluir valores personalizados
											</Typography>
											<Typography variant="body2" component="div">
												• Exemplo: "Olá {'{{nome}}'}, sua empresa {'{{empresa}}'} tem um desconto de {'{{valor}}'}"
											</Typography>
										</Box>
									</Box>
								</Grid>

								<Grid item xs={12}>
									<Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'medium' }}>
										👥 Seleção de Usuários
									</Typography>
								</Grid>

								<Grid item xs={12}>
									<Typography>{errors["users"]}</Typography>
								</Grid>
								<Grid item xs={12} md={5}>
									<Typography style={{ marginLeft: 16 }} variant="body2">
										Não enviar para:
									</Typography>
									<TextInput
										id="filter"
										variant="standard"
										name="filter"
										hideLabel
										placeholder="Filtro"
										value={filterOut}
										errors={errors}
										onChange={(id, v) => setFilterOut(v)}
									/>
									<List
										width={400}
										height={400}
										itemSize={36}
										itemCount={usersNotIn.length}
									>
										{props => renderRow(usersNotIn, props)}
									</List>
								</Grid>
								<Grid
									item
									xs={12}
									md={2}
									container
									justifyContent="center"
									alignItems="center"
									alignContent="center"
								>
									<Grid
										item
										xs={12}
										style={{ display: "flex", justifyContent: "center" }}
									>
										<IconButton
											disabled={!usersNotIn.length}
											onClick={handleRight}
										>
											<AiOutlineDoubleRight />
										</IconButton>
									</Grid>
									<Grid
										item
										xs={12}
										style={{ display: "flex", justifyContent: "center" }}
									>
										<IconButton disabled={!usersIn.length} onClick={handleLeft}>
											<AiOutlineDoubleLeft />
										</IconButton>
									</Grid>
								</Grid>
								<Grid item xs={12} md={5}>
									<Typography style={{ marginLeft: 16 }} variant="body2">
										Enviar para:
									</Typography>
									<TextInput
										id="filter"
										variant="standard"
										name="filter"
										hideLabel
										placeholder="Filtro"
										value={filterIn}
										errors={errors}
										onChange={(id, v) => setFilterIn(v)}
									/>
									<List
										width={400}
										height={400}
										itemSize={36}
										itemCount={usersIn.length || 1}
									>
										{props => renderRow(usersIn, props)}
									</List>
								</Grid>
							</Grid>
						</TabPanel>

						<Box sx={{ mt: 4, p: 3, bgcolor: 'grey.100', borderRadius: 2 }}>
							<Typography variant="h6" gutterBottom>
								📊 Resumo da Campanha
							</Typography>
							
							<Grid container spacing={2}>
								<Grid item xs={12} sm={6} md={3}>
									<Box sx={{ textAlign: 'center', p: 2, bgcolor: 'white', borderRadius: 1 }}>
										<Typography variant="h4">👥</Typography>
										<Typography variant="h6">{users?.length || 0}</Typography>
										<Typography variant="body2" color="text.secondary">Destinatários</Typography>
									</Box>
								</Grid>
								
								<Grid item xs={12} sm={6} md={3}>
									<Box sx={{ textAlign: 'center', p: 2, bgcolor: 'white', borderRadius: 1 }}>
										<Typography variant="h4">📧</Typography>
										<Typography variant="h6">{emailMessage ? '✅' : '❌'}</Typography>
										<Typography variant="body2" color="text.secondary">E-mail</Typography>
									</Box>
								</Grid>
								
								<Grid item xs={12} sm={6} md={3}>
									<Box sx={{ textAlign: 'center', p: 2, bgcolor: 'white', borderRadius: 1 }}>
										<Typography variant="h4">📱</Typography>
										<Typography variant="h6">{smsMessage ? '✅' : '❌'}</Typography>
										<Typography variant="body2" color="text.secondary">SMS</Typography>
									</Box>
								</Grid>
								
								<Grid item xs={12} sm={6} md={3}>
									<Box sx={{ textAlign: 'center', p: 2, bgcolor: 'white', borderRadius: 1 }}>
										<Typography variant="h4">💚</Typography>
										<Typography variant="h6">{whatsAppMessage ? '✅' : '❌'}</Typography>
										<Typography variant="body2" color="text.secondary">WhatsApp</Typography>
									</Box>
								</Grid>
								
								<Grid item xs={12}>
									<Box sx={{ textAlign: 'center', p: 2, bgcolor: 'white', borderRadius: 1 }}>
										<Typography variant="h4">🔔</Typography>
										<Typography variant="h6">{pushTitle && pushMessage ? '✅' : '❌'}</Typography>
										<Typography variant="body2" color="text.secondary">Push Notification</Typography>
									</Box>
								</Grid>
							</Grid>
						</Box>

						<Box sx={{ mt: 3, p: 2, display: 'flex', gap: 2, justifyContent: 'space-between', alignItems: 'center' }}>
							<Box>
								<Button
									variant="outlined"
									startIcon={'🗑️'}
									onClick={() => dispatch(clearValues())}
									sx={{ mr: 1 }}
								>
									Limpar Alterações
								</Button>
								<Button
									variant="outlined"
									startIcon={'💾'}
									onClick={() => {
										// TODO: Implement save as draft functionality
										alert('Funcionalidade "Salvar como Rascunho" será implementada em breve!');
									}}
								>
									Salvar como Rascunho
								</Button>
							</Box>
							
							<Box>
								<Button
									variant="contained"
									color="primary"
									size="large"
									startIcon={'✏️'}
									type="submit"
									disabled={!name || users?.length === 0}
									sx={{ 
										px: 4,
										py: 1.5,
										fontSize: '1.1rem'
									}}
								>
									Salvar Alterações
								</Button>
							</Box>
						</Box>
					</form>

					{/* Hidden file input for XLSX import */}
					<input
						ref={emailImportFileRef}
						style={{ display: "none" }}
						type="file"
						accept="application/excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.xlsx,.xls"
						onChange={handleEmailFileSelected}
					/>
				</Paper>
			</Grid>

			{/* Import Status Modal */}
			<ImportStatusModal
				open={importModalOpen}
				onClose={handleCloseImportModal}
				status={importStatus}
				progress={importProgress}
				results={importResults}
				error={importError}
			/>
		</Grid>
	);
}

export default MarketingEdit;
